import moment from 'moment';
import { formatDatePayload } from '@/core/hooks/use-date-time-hook';

/** Canonical filter date range used by dashboard + auth session. */
export interface FilterDateRange {
  year: number;
  startMonth: number;
  endMonth: number;
  fromDate: string;
  toDate: string;
  minDate: string;
}

export interface FilterDateRangeRaw {
  year?: number | string;
  start_month?: number | string;
  end_month?: number | string;
  startMonth?: number | string;
  endMonth?: number | string;
  from_date?: string;
  to_date?: string;
  fromDate?: string;
  toDate?: string;
  min_date?: string;
  minDate?: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toMonth(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1 || n > 12) {
    return fallback;
  }
  return Math.trunc(n);
}

function toYear(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 2000 || n > 2100) {
    return fallback;
  }
  return Math.trunc(n);
}

function lastDayOfMonth(year: number, month: number): string {
  return formatDatePayload(new Date(year, month, 0));
}

function firstDayOfMonth(year: number, month: number): string {
  return formatDatePayload(new Date(year, month - 1, 1));
}

/**
 * Resolve API from_date for a start month, never earlier than min_date.
 */
export function resolveFromDate(
  year: number,
  startMonth: number,
  minDate?: string | null
): string {
  const first = firstDayOfMonth(year, startMonth);
  if (minDate && minDate > first) {
    return minDate;
  }
  return first;
}

export function resolveToDate(year: number, endMonth: number): string {
  return lastDayOfMonth(year, endMonth);
}

export function buildFilterDateRange(input: {
  year: number;
  startMonth: number;
  endMonth: number;
  minDate?: string | null;
}): FilterDateRange {
  const year = toYear(input.year, new Date().getFullYear());
  const startMonth = toMonth(input.startMonth, 1);
  let endMonth = toMonth(input.endMonth, startMonth);
  if (endMonth < startMonth) {
    endMonth = startMonth;
  }

  const minDate =
    input.minDate && moment(input.minDate, 'YYYY-MM-DD', true).isValid()
      ? input.minDate
      : firstDayOfMonth(year, 1);

  const fromDate = resolveFromDate(year, startMonth, minDate);
  const toDate = resolveToDate(year, endMonth);

  return {
    year,
    startMonth,
    endMonth,
    fromDate,
    toDate,
    minDate,
  };
}

export function normalizeFilterDateRange(
  raw: unknown,
  fallbackYear?: number
): FilterDateRange | null {
  if (!isPlainObject(raw)) {
    return null;
  }

  const year = toYear(
    raw.year,
    fallbackYear ?? new Date().getFullYear()
  );
  const startMonth = toMonth(raw.start_month ?? raw.startMonth, 1);
  const endMonth = toMonth(raw.end_month ?? raw.endMonth, startMonth);
  const minDateRaw = raw.min_date ?? raw.minDate;
  const minDate =
    typeof minDateRaw === 'string' &&
    moment(minDateRaw, 'YYYY-MM-DD', true).isValid()
      ? minDateRaw
      : firstDayOfMonth(year, 1);

  const fromRaw = raw.from_date ?? raw.fromDate;
  const toRaw = raw.to_date ?? raw.toDate;

  const fromDate =
    typeof fromRaw === 'string' &&
    moment(fromRaw, 'YYYY-MM-DD', true).isValid()
      ? fromRaw
      : resolveFromDate(year, startMonth, minDate);

  const toDate =
    typeof toRaw === 'string' && moment(toRaw, 'YYYY-MM-DD', true).isValid()
      ? toRaw
      : resolveToDate(year, endMonth);

  return {
    year,
    startMonth,
    endMonth,
    fromDate,
    toDate,
    minDate,
  };
}

/**
 * Pull filter_date_range from login / me / abilities payloads.
 */
export function extractFilterDateRange(
  payload: unknown,
  fallbackYear?: number
): FilterDateRange | null {
  if (!isPlainObject(payload)) {
    return null;
  }

  const direct =
    payload.filter_date_range ?? payload.filterDateRange ?? null;
  const fromDirect = normalizeFilterDateRange(direct, fallbackYear);
  if (fromDirect) {
    return fromDirect;
  }

  const user = payload.user;
  if (isPlainObject(user)) {
    return normalizeFilterDateRange(
      user.filter_date_range ?? user.filterDateRange,
      fallbackYear
    );
  }

  return null;
}

/** Default range for a year: January → current month (or Dec if past year). */
export function buildDefaultFilterDateRange(year: number): FilterDateRange {
  const now = new Date();
  const endMonth =
    year === now.getFullYear() ? now.getMonth() + 1 : 12;
  return buildFilterDateRange({
    year,
    startMonth: 1,
    endMonth,
  });
}

export function monthRangeValue(
  range: FilterDateRange | null | undefined
): [string, string] | null {
  if (!range) {
    return null;
  }
  const start = `${range.year}-${String(range.startMonth).padStart(2, '0')}`;
  const end = `${range.year}-${String(range.endMonth).padStart(2, '0')}`;
  return [start, end];
}

export function filterDateRangeFromMonthValue(
  value: [string, string],
  minDate?: string | null,
  yearFallback?: number
): FilterDateRange | null {
  if (!value || value.length !== 2) {
    return null;
  }

  const [startYm, endYm] = value;
  const startParts = startYm.split('-').map(Number);
  const endParts = endYm.split('-').map(Number);
  if (startParts.length < 2 || endParts.length < 2) {
    return null;
  }

  const year = toYear(startParts[0], yearFallback ?? new Date().getFullYear());
  return buildFilterDateRange({
    year,
    startMonth: startParts[1],
    endMonth: endParts[1],
    minDate,
  });
}

export function filterDateRangesEqual(
  a: FilterDateRange | null | undefined,
  b: FilterDateRange | null | undefined
): boolean {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.year === b.year &&
    a.startMonth === b.startMonth &&
    a.endMonth === b.endMonth &&
    a.fromDate === b.fromDate &&
    a.toDate === b.toDate &&
    a.minDate === b.minDate
  );
}
